import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Star, ExternalLink } from 'lucide-react';
import { TESTIMONIALS } from '../mock';
import { useReducedMotion } from './OrgCarousel';

export const GOOGLE_READ_URL = 'https://www.google.com/maps/search/?api=1&query=Movevan%20LTD%2007985291097';
export const GOOGLE_WRITE_URL = 'https://g.page/r/CS3poVk4PH2vEAE/review?utm_source=gbp&utm_medium=reviews&utm_campaign=qr';

// Snapshot of genuine Google reviews, verified by the owner on 23 October 2026. Not a live feed.
const GOOGLE_REVIEWS = [
  { name: 'Norvia Matthew', stars: 5, text: 'Highly recommend, they took a last minute booking which was such a big help. Drivers were very friendly and were careful with the furniture and my house.' },
  { name: 'ofcmazza', stars: 5, text: 'remarkable service they left the place clean and spotless big thanks Darren will rec comment you guys to all friends and family thanks for getting me in so last minute and clearing the rubbish out.' },
  { name: 'Siri', stars: 5, text: 'They were exceptional with their services and I would recommend. Very helpful and friendly people. They were very swift and quick in moving the belongings into the van.' },
];

const REVIEWS = [
  ...GOOGLE_REVIEWS.map(r => ({ ...r, source: 'google' })),
  ...TESTIMONIALS.map(t => ({ name: t.name, stars: t.rating, text: t.text, source: 'website', role: t.role })),
];

const initials = (n) => n.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const ReviewCard = ({ r, i, total }) => (
  <article className="h-full rounded-2xl bg-white border border-slate-200 p-6 flex flex-col" aria-roledescription="slide" aria-label={`Review ${i + 1} of ${total}`} data-testid={`review-${r.source}-${i}`}>
    <div className="flex items-center gap-1" aria-label={`${r.stars} out of 5 stars`}>
      {[...Array(5)].map((_, k) => <Star key={k} aria-hidden="true" className={`w-4 h-4 ${k < r.stars ? 'fill-[color:var(--mv-yellow)] text-[color:var(--mv-yellow)]' : 'text-slate-300'}`} />)}
    </div>
    <p className="mt-4 text-slate-700 leading-relaxed flex-1">“{r.text}”</p>
    <div className="mt-5 flex items-center gap-3">
      <span className="w-10 h-10 rounded-full bg-[color:var(--mv-navy)] text-white text-sm font-bold flex items-center justify-center" aria-hidden="true">{initials(r.name)}</span>
      <div className="min-w-0">
        <div className="font-bold text-[color:var(--mv-navy)] truncate">{r.name}</div>
        <div className="text-xs text-slate-500" data-testid={`review-source-${i}`}>
          {r.source === 'google'
            ? <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" className="underline">Review on Google</a>
            : `Website testimonial${r.role ? ` · ${r.role}` : ''}`}
        </div>
      </div>
    </div>
  </article>
);

export const ReviewsCarousel = () => {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hover, setHover] = useState(false);
  const touch = useRef(null);
  const n = REVIEWS.length;
  const go = useCallback((d) => setIdx(i => (i + d + n) % n), [n]);
  const auto = !reduced && !paused && !hover;
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => go(1), 7000);
    return () => clearInterval(t);
  }, [auto, go]);
  const onKey = (e) => { if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); };
  const btn = 'h-11 w-11 rounded-full border border-slate-300 bg-white inline-flex items-center justify-center hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--mv-orange)]';

  return (
    <section className="py-8 md:py-14 bg-[color:var(--mv-sky)]" aria-labelledby="reviews-title" data-testid="reviews-carousel">
      <div className="container-xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mb-2 text-sm font-bold text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)]" aria-label="Five stars. Read our verified reviews on Google (opens in a new tab)" data-testid="reviews-stars-link">
              <span className="flex items-center gap-0.5" aria-hidden="true">{[...Array(5)].map((_, k) => <Star key={k} className="w-5 h-5 fill-[color:var(--mv-yellow)] text-[color:var(--mv-yellow)]" />)}</span>
              <span className="underline underline-offset-2">Reviews on Google</span>
            </a>
            <h2 id="reviews-title" className="font-display text-2xl md:text-4xl font-extrabold text-[color:var(--mv-navy)]">What customers say</h2>
            <p className="text-sm text-slate-600 mt-2">Google reviews shown here were copied from our Google Business Profile on 23 October 2026 — it isn't a live feed. Website testimonials were given to us directly and aren't Google reviews.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" className="btn-outline-dark" data-testid="reviews-read-google">Read all reviews on Google <ExternalLink className="w-4 h-4" /></a>
            <a href={GOOGLE_WRITE_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="reviews-leave-google">Leave a Google review</a>
          </div>
        </div>

        <div className="mt-5 md:mt-8" role="region" aria-roledescription="carousel" aria-label="Customer reviews" tabIndex={0} onKeyDown={onKey}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocus={() => setHover(true)} onBlur={() => setHover(false)}
          onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" aria-live={auto ? 'off' : 'polite'} data-testid="reviews-track">
            {[0, 1, 2].map(k => {
              const i = (idx + k) % n;
              return <div key={`${i}-${k}`} className={`${k === 1 ? 'hidden md:block' : ''} ${k === 2 ? 'hidden lg:block' : ''} mv-fade-in`}><ReviewCard r={REVIEWS[i]} i={i} total={n} /></div>;
            })}
          </div>
          <div className="mt-5 flex items-center justify-center gap-3">
            <button type="button" className={btn} onClick={() => go(-1)} aria-label="Previous review" data-testid="reviews-prev"><ChevronLeft className="w-5 h-5" /></button>
            {!reduced && (
              <button type="button" className={btn} onClick={() => setPaused(p => !p)} aria-label={paused ? 'Play reviews' : 'Pause reviews'} data-testid="reviews-toggle">
                {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
            )}
            <button type="button" className={btn} onClick={() => go(1)} aria-label="Next review" data-testid="reviews-next"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="mt-2 text-center text-xs text-slate-500" data-testid="reviews-position">{idx + 1} / {n}</div>
        </div>
      </div>
    </section>
  );
};
